import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCOP, formatDate } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { Input } from "../components/ui/input";

// ───────────────────────────────────────────────────────────────────────
// Bandeja: 5 tiles clickeables (Fase 10 — Track B.2)
// ───────────────────────────────────────────────────────────────────────
const INBOX_TILES = [
  { key: "payments_pending", label: "Pagos por conciliar", tone: "bg-rose-50 text-rose-900 border-rose-300", url: "/admin/payments?status=pending_confirmation" },
  { key: "generation_errors", label: "Casos con error de generación", tone: "bg-amber-50 text-amber-900 border-amber-300", url: "/admin/cases" },
  { key: "needs_reviewer", label: "Diagnósticos sin revisor", tone: "bg-orange-50 text-orange-900 border-orange-300", url: "/admin/reviews?reviewer=unassigned" },
  { key: "n2_pending", label: "Solicitudes Kit Documental", tone: "bg-emerald-50 text-emerald-900 border-emerald-300", url: "/admin/requests" },
  { key: "stuck_processing", label: "Casos atascados >10m", tone: "bg-sky-50 text-sky-900 border-sky-300", url: "/admin/cases" },
];

function AdminInbox() {
  const [counts, setCounts] = useState({ payments_pending: 0, generation_errors: 0, needs_reviewer: 0, n2_pending: 0, stuck_processing: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p1, p2, p3, p4, p5] = await Promise.all([
          api.get("/admin/cases?payment_status=pending_confirmation&limit=1"),
          api.get("/admin/cases?has_generation_error=true&limit=1"),
          api.get("/admin/cases?state=nivel1_needs_human_review&assigned_reviewer_id=unassigned&limit=1"),
          api.get("/admin/commercial-contact-requests?status_=pending&limit=1"),
          api.get("/admin/cases?stuck_in_processing=true&limit=1"),
        ]);
        if (!alive) return;
        setCounts({
          payments_pending: p1.data?.total ?? 0,
          generation_errors: p2.data?.total ?? 0,
          needs_reviewer: p3.data?.total ?? 0,
          n2_pending: p4.data?.total ?? 0,
          stuck_processing: p5.data?.total ?? 0,
        });
      } catch (err) {
        // Inbox counts load failure no es crítico (tiles muestran 0). Log dev.
        if (process.env.NODE_ENV === "development") {
          console.warn("AdminInbox counts load failed:", err);
        }
      }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <section className="mb-10" data-testid="admin-inbox">
      <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">Tu bandeja</p>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {INBOX_TILES.map((tile) => (
          <Link key={tile.key} to={tile.url} className={`border ${tile.tone} px-4 py-3 hover:opacity-90 transition-opacity`} data-testid={`inbox-tile-${tile.key}`}>
            <div className="font-serif text-3xl">{loading ? "—" : counts[tile.key]}</div>
            <div className="text-[11px] uppercase tracking-wider mt-1 leading-tight">{tile.label}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────
// KPIs piloto
// ───────────────────────────────────────────────────────────────────────
function MetricCard({ label, value, testid }) {
  return (
    <div className="border border-gray-200 bg-white px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 leading-tight">{label}</div>
      <div className="font-serif text-2xl text-gray-900">{value ?? "—"}</div>
    </div>
  );
}

function PilotMetricsPanel() {
  const [since, setSince] = useState("");
  const [m, setM] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const url = since ? `/admin/pilot/metrics?since=${since}` : `/admin/pilot/metrics`;
        const { data } = await api.get(url);
        if (alive) setM(data || {});
      } catch (err) {
        // Pilot metrics load failure → muestra "Cargando…" estable. Log dev.
        if (process.env.NODE_ENV === "development") {
          console.warn("PilotMetrics load failed:", err);
        }
      }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [since]);

  return (
    <section className="mb-10" data-testid="pilot-metrics-panel">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-3">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-1">Métricas del piloto</p>
          <h2 className="font-serif text-2xl text-gray-900">KPIs operativos</h2>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-widest text-gray-500">Desde</label>
          <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="rounded-sm h-9 w-44" data-testid="pilot-since" />
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-gray-500">Cargando…</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <MetricCard label="Usuarios totales" value={m.total_users} testid="pm-users" />
          <MetricCard label="Casos creados" value={m.total_cases_created} testid="pm-cases" />
          <MetricCard label="Diagnósticos generados" value={m.n1_diagnosis_generated_total} testid="pm-diag-gen" />
          <MetricCard label="Diagnósticos aprobados" value={m.n1_diagnosis_approved_total} testid="pm-diag-app" />
          <MetricCard label="Cambios solicitados" value={m.n1_revision_requested_total} testid="pm-diag-chg" />
          <MetricCard label="Descargas OK" value={m.n1_downloads_successful_total} testid="pm-dl-ok" />
          <MetricCard label="Descargas fallidas" value={m.n1_downloads_failed_total} testid="pm-dl-fail" />
          <MetricCard label="Pagos confirmados" value={m.payments_confirmed_total} testid="pm-pay-ok" />
          <MetricCard label="Pagos pendientes" value={m.payments_pending_total} testid="pm-pay-pend" />
          <MetricCard label="Ofertas Kit Doc." value={m.n2_offers_sent_total} testid="pm-n2-total" />
          <MetricCard label="Costo LLM acum." value={`USD ${(m.llm_cost_estimated_usd ?? 0).toFixed?.(4) ?? m.llm_cost_estimated_usd ?? "—"}`} testid="pm-llm-cost" />
          <MetricCard label="Apto Explorador" value={m.n0_results_distribution?.apto} testid="pm-sem-apto" />
        </div>
      )}
      {!loading && m.nps && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">NPS — Satisfacción</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            <MetricCard
              label="NPS Diagnóstico"
              value={m.nps.nivel1?.nps_score == null ? "—" : m.nps.nivel1.nps_score}
              testid="pilot-nps-nivel1-score"
            />
            <MetricCard
              label="Respuestas Diag."
              value={m.nps.nivel1?.total_responses ?? 0}
              testid="pilot-nps-nivel1-total"
            />
            <MetricCard
              label="Promotores Diag."
              value={m.nps.nivel1?.promoters_count ?? 0}
              testid="pilot-nps-nivel1-promoters"
            />
            <MetricCard
              label="NPS Kit Doc."
              value={m.nps.nivel2?.nps_score == null ? "—" : m.nps.nivel2.nps_score}
              testid="pilot-nps-nivel2-score"
            />
            <MetricCard
              label="Respuestas Kit"
              value={m.nps.nivel2?.total_responses ?? 0}
              testid="pilot-nps-nivel2-total"
            />
            <MetricCard
              label="Promotores Kit"
              value={m.nps.nivel2?.promoters_count ?? 0}
              testid="pilot-nps-nivel2-promoters"
            />
          </div>
        </div>
      )}
      {!loading && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">
            Funnel piloto (Fase 11.3.E)
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            <MetricCard label="Pagos N1 intentos" value={m.payments_n1_attempts ?? 0} testid="pm-pay-n1-att" />
            <MetricCard label="Pagos N1 conf." value={m.payments_n1_confirmed ?? 0} testid="pm-pay-n1-conf" />
            <MetricCard label="N1 entregados" value={m.n1_delivered_total ?? 0} testid="pm-n1-delivered" />
            <MetricCard label="Solicitudes Kit" value={m.n2_intake_submitted_total ?? 0} testid="pm-n2-requests" />
            <MetricCard label="Pagos N2 intentos" value={m.payments_n2_attempts ?? 0} testid="pm-pay-n2-att" />
            <MetricCard label="Pagos N2 conf." value={m.payments_n2_confirmed ?? 0} testid="pm-pay-n2-conf" />
            <MetricCard label="Kits aprobados" value={m.n2_kit_approved_total ?? 0} testid="pm-n2-approved" />
            <MetricCard label="Kits entregados" value={m.n2_delivered_total ?? 0} testid="pm-n2-delivered" />
            <MetricCard
              label="Cycle N1 (min)"
              value={m.cycle_time_minutes_avg?.payment_confirm_to_n1_delivered ?? "—"}
              testid="pm-cycle-n1"
            />
            <MetricCard
              label="Cycle N2 (min)"
              value={m.cycle_time_minutes_avg?.payment_confirm_to_n2_delivered ?? "—"}
              testid="pm-cycle-n2"
            />
          </div>
        </div>
      )}
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────
// "Latest 5" lists
// ───────────────────────────────────────────────────────────────────────
function LatestCasesList() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/admin/cases?limit=5").then(({ data }) => alive && setItems(data.items || [])).catch(() => {});
    return () => { alive = false; };
  }, []);
  return (
    <div className="bg-white border border-gray-200" data-testid="latest-cases">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-widest text-gray-500">Últimos casos</h3>
        <Link to="/admin/cases" className="text-[11px] text-primary hover:underline" data-testid="latest-cases-all">Ver todos →</Link>
      </div>
      {items.length === 0 && <p className="text-sm text-gray-500 p-4">Sin casos.</p>}
      <ul className="divide-y divide-gray-100">
        {items.map((c) => (
          <li key={c.id} className="px-4 py-2 hover:bg-gray-50 text-sm flex items-center justify-between gap-2">
            <Link to={`/admin/cases/${c.id}`} className="text-gray-900 hover:underline truncate flex-1">{c.title}</Link>
            <span className="text-[11px] text-gray-500 font-mono whitespace-nowrap">{c.state}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LatestPaymentsList() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/admin/payments?limit=5&status=pending_confirmation").then(({ data }) => alive && setItems(data.items || [])).catch(() => {});
    return () => { alive = false; };
  }, []);
  return (
    <div className="bg-white border border-gray-200" data-testid="latest-payments">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-widest text-gray-500">Pagos pendientes</h3>
        <Link to="/admin/payments" className="text-[11px] text-primary hover:underline" data-testid="latest-payments-all">Conciliar →</Link>
      </div>
      {items.length === 0 && <p className="text-sm text-gray-500 p-4">Sin pagos pendientes.</p>}
      <ul className="divide-y divide-gray-100">
        {items.map((p) => (
          <li key={p.id} className="px-4 py-2 hover:bg-gray-50 text-sm flex items-center justify-between gap-2">
            <Link to={`/admin/cases/${p.case_id}`} className="text-gray-900 hover:underline truncate flex-1">{p.case_title || p.reference}</Link>
            <span className="text-[11px] whitespace-nowrap">{formatCOP(p.amount_cop || p.monto_cop)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LatestRequestsList() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/admin/requests?source=all&limit=5").then(({ data }) => alive && setItems(data.items || [])).catch(() => {});
    return () => { alive = false; };
  }, []);
  return (
    <div className="bg-white border border-gray-200" data-testid="latest-requests">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-widest text-gray-500">Últimas solicitudes</h3>
        <Link to="/admin/requests" className="text-[11px] text-primary hover:underline" data-testid="latest-requests-all">Ver bandeja →</Link>
      </div>
      {items.length === 0 && <p className="text-sm text-gray-500 p-4">Sin solicitudes recientes.</p>}
      <ul className="divide-y divide-gray-100">
        {items.map((r) => (
          <li key={`${r.source}-${r.id}`} className="px-4 py-2 hover:bg-gray-50 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-900 truncate flex-1">{r.title}</span>
              <span className="text-[10px] uppercase tracking-wider text-gray-500 whitespace-nowrap">{r.source}</span>
            </div>
            <div className="text-[11px] text-gray-500 truncate">{r.description}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Main dashboard (refactor Fase 10.5 Entrega 3 — solo KPIs + bandeja + latest 5)
// ───────────────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const { user } = useAuth();
  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8 pb-5 border-b border-gray-200">
        <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">Panel administrativo</p>
        <h1 className="font-serif text-3xl text-gray-900">Bienvenido, {user?.name || "admin"}</h1>
        <p className="text-sm text-gray-600 mt-1">
          Vista resumen. Para gestión completa entra a Casos, Pagos, Revisiones, Solicitudes, Configuración, Costos o Auditoría.
        </p>
      </div>

      <AdminInbox />
      <PilotMetricsPanel />

      <section className="mb-10">
        <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-3">Resumen rápido</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <LatestCasesList />
          <LatestPaymentsList />
          <LatestRequestsList />
        </div>
      </section>
    </div>
  );
}
